import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { CombatGrid } from '../../components/CombatGrid/CombatGrid';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useToast, ToastContainer } from '../../components/Toast/Toast';
import { spriteApi, missionAdminApi, type SpriteEntry, type MissionData } from '../../api/adminApi';
import { NORTH_SIDES } from '../../types/adminTypes';

const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN ?? 'darkage-admin-dev-token';

type ToolType = 'walkable' | 'dirt' | 'obstacle' | 'water' | 'tree' | 'spawn' | 'battle_point' | 'mission_portal' | 'npc_blacksmith' | 'npc_merchant'
  | 'elev_0' | 'elev_1' | 'elev_2' | 'climb_ramp' | 'climb_stairs' | 'climb_rope';

// Relevo: pincel de Nível (só muda a altura, preserva o tipo da célula) e
// Via de Subida em 2 cliques (Ponto A → vizinha ortogonal de outro nível),
// gravada nas duas pontas — mesma regra do editor de mapas de batalha.
const ELEV_TOOLS: Partial<Record<ToolType, number>> = { elev_0: 0, elev_1: 1, elev_2: 2 };
const CLIMB_TOOLS: Partial<Record<ToolType, 'RAMP' | 'STAIRS' | 'ROPE'>> = {
  climb_ramp: 'RAMP', climb_stairs: 'STAIRS', climb_rope: 'ROPE',
};
const CLIMB_ICON: Record<string, string> = { RAMP: '↗', STAIRS: '🪜', ROPE: '🪢' };

/** Nível (altura) só em terreno caminhável ou Chão Batido — lago/obstáculo/NPC ficam no chão. */
export const hubAcceptsElevation = (type?: string) => !type || type === 'walkable' || type === 'dirt';

// Papéis de NPC do Hub cuja arte é escolhível no painel (mesma lista do backend
// em HubController::resolveNpcSprites). Exportado — reusado em NpcConfigPage.
export const HUB_NPC_ROLES: Array<{ role: 'blacksmith' | 'guardian' | 'merchant'; icon: string; label: string; cellType?: string }> = [
  { role: 'blacksmith', icon: '🧔',   label: 'NPC Ferreiro',            cellType: 'npc_blacksmith' },
  { role: 'guardian',   icon: '🌀',   label: 'NPC Guardião do Portal',  cellType: 'battle_point' },
  { role: 'merchant',   icon: '🛒',   label: 'NPC Mercadora',           cellType: 'npc_merchant' },
];

// ── Categorias de ferramentas ──────────────────────────────────────────────
const TOOL_CATEGORIES = [
  {
    label: '🗺️ Organização do Mapa',
    tools: [
      { id: 'walkable'  as ToolType, color: '#3a5e3a', icon: '🟩', label: 'Caminhável' },
      { id: 'dirt'      as ToolType, color: '#7a5a36', icon: '🟫', label: 'Chão Batido' },
      { id: 'obstacle'  as ToolType, color: '#444',    icon: '🕳️', label: 'Obstáculo' },
      { id: 'water'     as ToolType, color: '#00aaff', icon: '💧', label: 'Água' },
      { id: 'tree'      as ToolType, color: '#1b4d1b', icon: '🌳', label: 'Árvore' },
      { id: 'spawn'     as ToolType, color: '#ff4444', icon: '📍', label: 'Ponto Inicial' },
    ]
  },
  {
    label: '⛰️ Relevo',
    tools: [
      { id: 'elev_0'       as ToolType, color: '#3a5e3a', icon: '0️⃣', label: 'Nível 0 (chão)' },
      { id: 'elev_1'       as ToolType, color: '#6f7d70', icon: '1️⃣', label: 'Nível 1 (platô)' },
      { id: 'elev_2'       as ToolType, color: '#93a394', icon: '2️⃣', label: 'Nível 2 (cume)' },
      { id: 'climb_ramp'   as ToolType, color: '#8b5a2b', icon: '↗',  label: 'Rampa' },
      { id: 'climb_stairs' as ToolType, color: '#c08a52', icon: '🪜', label: 'Escada' },
      { id: 'climb_rope'   as ToolType, color: '#a9814a', icon: '🪢', label: 'Corda' },
    ]
  },
  {
    label: '⚔️ Batalhas',
    tools: [
      { id: 'battle_point' as ToolType, color: '#ff0044', icon: '🌀', label: 'Portal de Batalha' },
      { id: 'mission_portal' as ToolType, color: '#7a3cff', icon: '🔮', label: 'Portal de Missão' },
    ]
  },
  {
    label: '💬 NPCs',
    tools: [
      { id: 'npc_blacksmith' as ToolType, color: '#d4a033', icon: '🧔', label: 'NPC Ferreiro' },
      { id: 'npc_merchant' as ToolType, color: '#c9922e', icon: '🛒', label: 'NPC Mercadora' },
    ]
  }
];

export default function HubMapAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [config, setConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sprites, setSprites] = useState<SpriteEntry[]>([]);
  const { messages, toast, remove } = useToast();

  // Tools for Grid Editor
  const [activeTool, setActiveTool] = useState<ToolType>('walkable');
  // Via de Subida: célula A escolhida, aguardando o 2º clique (B).
  const [climbPendingA, setClimbPendingA] = useState<{ x: number; y: number } | null>(null);
  // Portal de Missão: missão gravada na célula ao pintar ('' = jogador escolhe).
  const [missions, setMissions] = useState<MissionData[]>([]);
  const [portalMissionId, setPortalMissionId] = useState('');

  useEffect(() => {
    fetch('/api/admin/hub/config', {
      headers: { 'X-Admin-Token': ADMIN_TOKEN }
    })
      .then(r => r.json())
      .then(data => {
        // PHP serializa um mapa vazio como `[]` — normaliza para objeto.
        const npcSprites = data?.npcSprites && !Array.isArray(data.npcSprites) ? data.npcSprites : {};
        setConfig({ ...data, npcSprites });
        setLoading(false);
      })
      .catch(console.error);
    spriteApi.list().then(setSprites).catch(console.error);
    missionAdminApi.list().then(setMissions).catch(console.error);
  }, []);

  // Espelha HubController::resolveNpcSprites no cliente para o Preview Isométrico
  // mostrar exatamente a arte que o jogador vai ver no Hub.
  const previewNpcSprites = useMemo(() => {
    const byName = new Map(sprites.map(s => [s.name, s]));
    const out: Record<string, { sprite: string; cols: number; rows: number; scale: number; offset_x: number; offset_y: number }> = {};
    for (const { role } of HUB_NPC_ROLES) {
      const chosen = config?.npcSprites?.[role];
      const s = chosen ? byName.get(chosen) : undefined;
      if (!s) continue;
      out[role] = {
        sprite: s.filename || `${s.name}.png`,
        cols: s.cols || 3,
        rows: s.rows || 8,
        scale: s.scale || 1,
        offset_x: s.offsetX || 0,
        offset_y: s.offsetY || 0,
      };
    }
    return out;
  }, [sprites, config]);

  const handleSave = async () => {
    setSaving(true);
    await fetch('/api/admin/hub/config', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN
      },
      body: JSON.stringify(config)
    });
    setSaving(false);
    toast.success('Salvo com sucesso!');
  };

  const handleCellClick = (x: number, y: number) => {
    const key = `${x}_${y}`;
    const newCells = { ...config.cells };
    // Remove a entrada se ela ficou vazia (célula padrão: caminhável, nível 0).
    const prune = (k: string) => { if (newCells[k] && Object.keys(newCells[k]).length === 0) delete newCells[k]; };

    const elevLevel = ELEV_TOOLS[activeTool];
    if (elevLevel !== undefined) {
      if (!hubAcceptsElevation(newCells[key]?.type)) return; // só terreno caminhável
      const c = { ...(newCells[key] || {}) };
      if (elevLevel === 0) delete c.elevation; else c.elevation = elevLevel;
      newCells[key] = c;
      prune(key);
      setConfig({ ...config, cells: newCells });
      return;
    }

    const climbType = CLIMB_TOOLS[activeTool];
    if (climbType) {
      const a = climbPendingA;
      if (!a) { setClimbPendingA({ x, y }); return; }
      if (a.x === x && a.y === y) { setClimbPendingA(null); return; }          // clicou de novo em A: cancela
      if (Math.abs(a.x - x) + Math.abs(a.y - y) !== 1) { setClimbPendingA({ x, y }); return; } // não vizinha: recomeça

      const keyA = `${a.x}_${a.y}`;
      const cA = { ...(newCells[keyA] || {}) };
      const cB = { ...(newCells[key] || {}) };
      if (!hubAcceptsElevation(cA.type) || !hubAcceptsElevation(cB.type)) {
        toast.error('A Via de Subida só liga células caminháveis (ou Chão Batido).');
        setClimbPendingA(null);
        return;
      }
      if (Number(cA.elevation ?? 0) === Number(cB.elevation ?? 0)) {
        toast.error('As duas células precisam estar em níveis diferentes.');
        setClimbPendingA(null);
        return;
      }
      const already = cA.climb_connection?.targetX === x && cA.climb_connection?.targetY === y;
      if (already) {
        delete cA.climb_connection;
        delete cB.climb_connection;
      } else {
        cA.climb_connection = { targetX: x, targetY: y, type: climbType };
        cB.climb_connection = { targetX: a.x, targetY: a.y, type: climbType };
      }
      newCells[keyA] = cA;
      newCells[key] = cB;
      prune(keyA); prune(key);
      setClimbPendingA(null);
      setConfig({ ...config, cells: newCells });
      return;
    }

    if (activeTool === 'spawn') {
      setConfig({ ...config, spawnX: x, spawnY: y });
      return;
    }

    if (activeTool === 'npc_blacksmith' || activeTool === 'npc_merchant') {
      // NPC único no mapa (um Ferreiro, uma Mercadora) — remove o anterior do
      // mesmo tipo (inclui o legado 'npc_farmer', substituído por 'npc_merchant').
      const sameRole = activeTool === 'npc_merchant'
        ? ['npc_merchant', 'npc_farmer']
        : ['npc_blacksmith'];
      Object.keys(newCells).forEach(k => {
        if (sameRole.includes(newCells[k].type)) {
          delete newCells[k].type;
          if (Object.keys(newCells[k]).length === 0) delete newCells[k];
        }
      });
      newCells[key] = { ...(newCells[key] || {}), type: activeTool };
    } else {
      if (!newCells[key]) newCells[key] = {};

      // Cópia: não muta o objeto da célula do estado anterior.
      newCells[key] = { ...newCells[key] };
      if (activeTool === 'walkable') {
        delete newCells[key].type;   // preserva nível/Via de Subida
        prune(key);
      } else {
        newCells[key].type = activeTool;
      }
      // missionId só existe em Portal de Missão (null = jogador escolhe).
      if (activeTool === 'mission_portal') newCells[key].missionId = portalMissionId || null;
      else if (newCells[key]) delete newCells[key].missionId;
    }

    // Tipo que não aceita nível: volta ao chão e perde a Via de Subida
    // (inclusive a ponta na vizinha que apontava para cá).
    if (newCells[key] && !hubAcceptsElevation(newCells[key].type)) {
      const link = newCells[key].climb_connection;
      delete newCells[key].elevation;
      delete newCells[key].climb_connection;
      if (link) {
        const pk = `${link.targetX}_${link.targetY}`;
        const p = newCells[pk];
        if (p?.climb_connection?.targetX === x && p.climb_connection.targetY === y) {
          newCells[pk] = { ...p };
          delete newCells[pk].climb_connection;
          prune(pk);
        }
      }
    }

    setConfig({ ...config, cells: newCells });
  };

  const applyDimensions = () => {
    const w = Math.max(5, Math.min(30, config.width));
    const h = Math.max(5, Math.min(40, config.height));
    setConfig({ ...config, width: w, height: h });
  };

  if (loading) return <div>Carregando...</div>;

  // Grid builder
  const grid = [];
  for (let y = 0; y < config.height; y++) {
    for (let x = 0; x < config.width; x++) {
      const key = `${x}_${y}`;
      const cell = config.cells[key] || {};
      const isSpawn = config.spawnX === x && config.spawnY === y;

      let content = null;
      let bgColor = '#3a5e3a';
      if (cell.type === 'dirt') bgColor = '#7a5a36';
      if (cell.type === 'obstacle') {
        content = '🕳️';
        bgColor = '#444';
      }
      if (cell.type === 'water') {
        content = '💧';
        bgColor = '#00aaff';
      }
      if (cell.type === 'tree') {
        content = '🌳';
        bgColor = '#1b4d1b';
      }
      if (cell.type === 'battle_point') {
        content = '🌀';
        bgColor = '#880033';
      }
      if (cell.type === 'mission_portal') {
        content = '🔮';
        bgColor = '#4a1f99';
      }
      if (cell.type === 'npc_blacksmith') {
        content = '🧔';
        bgColor = '#d4a033';
      }
      if (cell.type === 'npc_merchant' || cell.type === 'npc_farmer') {
        content = '🛒';
        bgColor = '#c9922e';
      }
      if (isSpawn) content = '📍';

      // Relevo: nível clareia a célula (pedra) e ganha um selo ▲N; Via de
      // Subida mostra o ícone do conector; ponto A pendente fica destacado.
      const elev = hubAcceptsElevation(cell.type) ? Number(cell.elevation ?? 0) : 0;
      if (elev > 0 && (!cell.type || cell.type === 'dirt')) bgColor = elev >= 2 ? '#93a394' : '#6f7d70';
      const climbIcon = cell.climb_connection ? CLIMB_ICON[cell.climb_connection.type] : null;
      const isPendingA = climbPendingA?.x === x && climbPendingA?.y === y;

      grid.push(
        <div
          key={key}
          className="mapCell"
          title={`(${x}, ${y}) — ${cell.type || 'walkable'} · nível ${elev}${cell.climb_connection ? ` · ${cell.climb_connection.type} → (${cell.climb_connection.targetX}, ${cell.climb_connection.targetY})` : ''}${isSpawn ? ' [SPAWN]' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); handleCellClick(x, y); }}
          // Arrastar pinta tipo/nível; a Via de Subida é só por clique.
          onMouseEnter={(e) => { if (e.buttons === 1 && !CLIMB_TOOLS[activeTool]) handleCellClick(x, y); }}
          style={{
            position: 'relative',
            outline: isPendingA ? '2px solid #f0d888' : undefined,
            outlineOffset: -2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            userSelect: 'none',
            backgroundColor: bgColor,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            width: '28px',
            height: '28px'
          }}
        >
          {content && <span style={{ fontWeight: 'bold', color: isSpawn ? '#ff4444' : 'inherit' }}>{content}</span>}
          {elev > 0 && (
            <span style={{ position: 'absolute', top: 0, left: 1, fontSize: 8, fontWeight: 800, color: '#fff', textShadow: '0 0 2px #000' }}>▲{elev}</span>
          )}
          {climbIcon && (
            <span style={{ position: 'absolute', bottom: -1, right: 0, fontSize: 10 }}>{climbIcon}</span>
          )}
        </div>
      );
    }
  }

  return (
    <>
      <div className="adminHeader">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btnGhost" onClick={() => navigate('/admin/maps')} title={t('common.cancel')}>
            ←
          </button>
          <h1>Gerenciador do Mapa Inicial (Hub)</h1>
        </div>
        <button className="btn btnPrimary" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving' as any) : t('common.save')}
        </button>
      </div>

      <div className="adminContent">
        <div className="adminCard">
          <div className="adminCardHeader">
            <span>{t('hubAdmin.visualSettings' as any)}</span>
          </div>
          <div className="formGrid" style={{ padding: '16px' }}>
            <div className="formGroup formGroupFull" style={{ marginBottom: 0 }}>
              <label className="formLabel">{t('hubAdmin.viewModeLabel' as any)}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {([
                  { value: 'layout', label: t('hubAdmin.viewModeLayout' as any) || 'Layout', icon: '🧩' },
                  { value: 'map',    label: t('hubAdmin.viewModeMap' as any) || 'Mapa',     icon: '🗺️' },
                ]).map(opt => {
                  const active = config.initialViewMode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setConfig({ ...config, initialViewMode: opt.value })}
                      style={{
                        flex: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: `1px solid ${active ? '#c8a040' : 'rgba(255,255,255,0.15)'}`,
                        background: active ? 'rgba(200,160,64,0.18)' : 'rgba(255,255,255,0.04)',
                        color: active ? '#f0d888' : '#bbb',
                        fontWeight: active ? 800 : 600,
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'background .15s, border-color .15s, color .15s',
                      }}
                    >
                      <span>{opt.icon}</span> {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Norte da bússola: qual lado do mapa (visão de cima) é o Norte */}
            <div className="formGroup formGroupFull" style={{ marginTop: 16, marginBottom: 0 }}>
              <label className="formLabel">{t('compass.northSide')}</label>
              <select
                className="formInput"
                value={config.north ?? 0}
                onChange={(e) => setConfig({ ...config, north: Number(e.target.value) })}
              >
                {NORTH_SIDES.map((side) => (
                  <option key={side} value={side}>{t(`compass.side.${side}`)}</option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div className="formGroup" style={{ marginTop: 16 }}>
              <label className="formLabel">Largura (Width)</label>
              <input
                type="number"
                className="formInput"
                value={config.width}
                min={5} max={30}
                onChange={(e) => setConfig({...config, width: parseInt(e.target.value, 10) || 5})}
              />
            </div>
            <div className="formGroup" style={{ marginTop: 16 }}>
              <label className="formLabel">Altura (Height)</label>
              <input
                type="number"
                className="formInput"
                value={config.height}
                min={5} max={40}
                onChange={(e) => setConfig({...config, height: parseInt(e.target.value, 10) || 5})}
              />
            </div>
            <div className="formGroup" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <label className="formLabel" style={{ visibility: 'hidden' }}>_</label>
              <button type="button" className="btn btnSecondary" onClick={applyDimensions}>
                Aplicar Dimensão
              </button>
            </div>
          </div>
        </div>

        <div className="adminCard" style={{ marginTop: 20 }}>
          <div className="adminCardHeader">
            <span>{t('hubAdmin.builder' as any)}</span>
          </div>
          
          <div style={{ padding: '14px 18px' }}>
            <div className="mapEditorLayout">
              {/* Editor sidebar — Organizado por categorias */}
              <div className="mapEditorSidebar">
                {CLIMB_TOOLS[activeTool] && (
                  <div style={{ padding: '6px 10px', marginBottom: 10, fontSize: 11, fontWeight: 600, borderRadius: 6, background: 'rgba(240,216,136,0.1)', color: climbPendingA ? '#f0d888' : '#bbb' }}>
                    {climbPendingA
                      ? `Agora clique na célula vizinha de outro nível (A: ${climbPendingA.x}, ${climbPendingA.y}). Clicar numa via existente a remove.`
                      : 'Via de Subida: clique na célula A e depois na vizinha (em outro nível).'}
                  </div>
                )}
                {TOOL_CATEGORIES.map((cat) => (
                  <div className="adminCard" key={cat.label} style={{ marginBottom: 10 }}>
                    <div className="adminCardHeader" style={{ fontSize: 11, padding: '6px 10px', background: 'rgba(255,255,255,0.04)' }}>
                      {cat.label}
                    </div>
                    <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {cat.tools.map((tool) => (
                        <button
                          key={tool.id}
                          type="button"
                          className={`brushBtn${activeTool === tool.id ? ' selected' : ''}`}
                          onClick={() => { setActiveTool(tool.id); setClimbPendingA(null); }}
                          style={{ fontSize: 12 }}
                        >
                          <span className="brushSwatch" style={{ background: tool.color }} />
                          {tool.icon} {tool.label}
                        </button>
                      ))}
                      {cat.tools.some(tool => tool.id === 'mission_portal') && activeTool === 'mission_portal' && (
                        <select className="formInput" style={{ fontSize: 12 }} value={portalMissionId}
                                onChange={e => setPortalMissionId(e.target.value)}>
                          <option value="">🎯 Jogador escolhe a missão</option>
                          {missions.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Grid */}
              <div className="mapEditorPanel" style={{ overflow: 'auto' }}>
                <div className="mapGrid" style={{ gridTemplateColumns: `repeat(${config.width}, 28px)` }}>
                  {grid}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Aparência dos NPCs do Hub ── */}
        {/* A escolha em si mudou pra tela dedicada (sidebar → 🧑‍🌾 NPCs), que
            dá mais espaço e preview maior; aqui fica só um atalho — o preview
            isométrico abaixo já reflete o que estiver salvo em config.npcSprites. */}
        <div className="adminCard" style={{ marginTop: 20 }}>
          <div className="adminCardHeader">Aparência dos NPCs do Hub</div>
          <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <p style={{ fontSize: 13, opacity: 0.75, margin: 0 }}>
              Qual sprite cada NPC do Hub usa agora se configura na tela dedicada de NPCs.
            </p>
            <button type="button" className="btn btnSecondary" onClick={() => navigate('/admin/npcs')}>
              🧑‍🌾 Configurar NPCs
            </button>
          </div>
        </div>

        {/* ── Preview ── */}
        <div className="adminCard" style={{ marginTop: 20 }}>
          <div className="adminCardHeader">Preview Isométrico</div>
          {/* `display:flex` é necessário pro `.wrapper` (flex:1) do CombatGrid
              preencher esta altura fixa — sem isso o canvas cai pro tamanho
              intrínseco padrão (300×150px), ficando minúsculo. */}
          <div style={{ height: 520, position: 'relative', display: 'flex' }}>
           <ErrorBoundary label="Preview Isométrico">
            <CombatGrid
              map={{
                width: config.width,
                height: config.height,
                north: config.north ?? 0,
                cells: Array.from({ length: config.height }, (_, y) =>
                  Array.from({ length: config.width }, (_, x) => {
                    const key = `${x}_${y}`;
                    const t = config.cells[key]?.type;
                    let cType = 'Walkable';
                    if (t === 'obstacle') cType = 'Full_Obstacle';
                    if (t === 'water') cType = 'Lake';
                    if (t === 'tree') cType = 'Tree';
                    if (t === 'battle_point') cType = 'battle_point';
                    if (t === 'npc_blacksmith') cType = 'npc_blacksmith';
                    if (t === 'npc_merchant' || t === 'npc_farmer') cType = 'npc_merchant';
                    return {
                      x, y, type: cType as any,
                      ...(t === 'dirt' ? { floor: 'dirt' as const } : {}),
                      elevation: hubAcceptsElevation(t) ? Number(config.cells[key]?.elevation ?? 0) : 0,
                      climb_connection: config.cells[key]?.climb_connection ?? null,
                    };
                  })
                )
              }}
              entities={{}}
              myEntityId={null}
              highlights={[]}
              cellHover={null}
              rotationMode="snap90"
              birdSpawner={null}
              npcSprites={previewNpcSprites}
              onCellClick={() => {}}
              onCellHover={() => {}}
              onMouseLeave={() => {}}
              turnSecondsLeft={null}
            />
           </ErrorBoundary>
          </div>
        </div>
      </div>
      <ToastContainer messages={messages} onRemove={remove} />
    </>
  );
}
